// Terms Settings Panel — A-01 email switch + sender fields (visible to all process members,
// editable only by owners and platform admins) + Review settings (owners/admins only).
import { useEffect, useState } from "react";
import api from "@/lib/api";

const inp = "w-full rounded-md border border-[#cfd4dc] px-3 py-2 text-sm disabled:bg-[#f5f6f8] disabled:text-[#6b7280]";
const split = (s) => s.split(/[\s,]+/).map((x) => x.trim()).filter(Boolean);

function A01Section({ slug, canEdit }) {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState(null);
  const [loadErr, setLoadErr] = useState(null);

  useEffect(() => {
    api.get("/terms/settings", { params: { tenant_slug: slug } })
      .then(({ data }) => setS({
        enabled: !!data.terms_email_a01_enabled,
        senderName: data.a01_sender_name || "",
        senderTitle: data.a01_sender_title || "",
        arEmail: data.a01_ar_email || "",
        officePhone: data.a01_office_phone || "",
      }))
      .catch((e) => {
        const status = e?.response?.status;
        setLoadErr(status === 403 ? "Settings are managed by owners and platform admins." : String(e?.response?.data?.detail || e.message));
      });
  }, [slug]);

  const save = async () => {
    if (!s) return;
    setMsg(null);
    if (s.enabled) {
      const missing = [];
      if (!s.senderName.trim()) missing.push("sender name");
      if (!s.senderTitle.trim()) missing.push("sender title");
      if (!s.arEmail.trim()) missing.push("AR email");
      if (!s.officePhone.trim()) missing.push("office phone");
      if (missing.length) { setMsg(`Cannot enable: missing ${missing.join(", ")}.`); return; }
    }
    try {
      await api.patch("/terms/settings", {
        terms_email_a01_enabled: s.enabled,
        a01_sender_name: s.senderName || null,
        a01_sender_title: s.senderTitle || null,
        a01_ar_email: s.arEmail || null,
        a01_office_phone: s.officePhone || null,
      }, { params: { tenant_slug: slug } });
      setMsg("Saved.");
    } catch (e) { setMsg(String(e?.response?.data?.detail || e.message)); }
  };

  if (loadErr) return <div className="text-xs text-[#6b7280]" data-testid="a01-load-err">{loadErr}</div>;
  if (!s) return null;

  return (
    <div className="space-y-3" data-testid="a01-section">
      <div className="text-xs font-semibold text-[#374151] uppercase tracking-widest">A-01 Application received email</div>
      <div className="flex items-start gap-3">
        <label className="flex items-center gap-2 cursor-pointer" data-testid="a01-toggle-label">
          {canEdit ? (
            <input type="checkbox" className="w-4 h-4 accent-[#0072bc]" checked={s.enabled}
              onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="a01-enabled-toggle" />
          ) : (
            <input type="checkbox" className="w-4 h-4" checked={s.enabled} readOnly disabled data-testid="a01-enabled-toggle" />
          )}
          <span className="text-sm font-medium">{s.enabled ? "ON" : "OFF"}</span>
        </label>
        <p className="text-xs text-[#8a6a1c] bg-[#fff8e6] border border-[#f5d98b] rounded px-2 py-1 flex-1" data-testid="a01-warning">
          When ON, every new application sends the A-01 email to the applicant. Leave OFF until Brett approves.
        </p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="block text-xs">Sender name
          <input className={inp} value={s.senderName} disabled={!canEdit}
            onChange={(e) => setS({ ...s, senderName: e.target.value })} data-testid="a01-sender-name" />
        </label>
        <label className="block text-xs">Sender title
          <input className={inp} value={s.senderTitle} disabled={!canEdit}
            onChange={(e) => setS({ ...s, senderTitle: e.target.value })} data-testid="a01-sender-title" />
        </label>
        <label className="block text-xs">AR email
          <input className={inp} type="email" value={s.arEmail} disabled={!canEdit}
            onChange={(e) => setS({ ...s, arEmail: e.target.value })} data-testid="a01-ar-email" />
        </label>
        <label className="block text-xs">Office phone
          <input className={inp} value={s.officePhone} disabled={!canEdit}
            onChange={(e) => setS({ ...s, officePhone: e.target.value })} data-testid="a01-office-phone" />
        </label>
      </div>
      {canEdit && (
        <div className="flex items-center gap-3">
          <button className="px-4 py-1.5 rounded-full bg-[#0072bc] text-white text-sm font-semibold" onClick={save} data-testid="a01-save-btn">Save A-01 settings</button>
          {msg && <span className="text-xs" data-testid="a01-msg">{msg}</span>}
        </div>
      )}
    </div>
  );
}

export function TermsSettingsPanel({ slug, canEdit = false }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState(null);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!open || f || !canEdit) return;
    api.get("/terms/settings", { params: { tenant_slug: slug } }).then(({ data }) => setF({
      reviewers: data.terms_reviewer_emails.join(", "), approvers: data.terms_approver_emails.join(", "),
      reasons: data.terms_denial_reasons, window: data.cycle_window_start_md }));
  }, [open, f, slug, canEdit]);

  const save = async () => {
    setMsg(null);
    try {
      await api.patch("/terms/settings", { terms_reviewer_emails: split(f.reviewers), terms_approver_emails: split(f.approvers),
        cycle_window_start_md: f.window }, { params: { tenant_slug: slug } });
      setMsg("Saved.");
    } catch (e) { setMsg(String(e?.response?.data?.detail || e.message)); }
  };

  return (
    <div className="space-y-4" data-testid="terms-settings-panel">
      <div className="bg-white border border-[#e5e7eb] rounded-lg p-4">
        <A01Section slug={slug} canEdit={canEdit} />
      </div>
      {canEdit && (
        <div className="bg-white border border-[#e5e7eb] rounded-lg" data-testid="terms-review-settings">
          <button className="w-full text-left px-4 py-3 text-sm font-semibold" onClick={() => setOpen(!open)} data-testid="terms-settings-toggle">{open ? "▾" : "▸"} Review settings</button>
          {open && f && (
            <div className="px-4 pb-4 space-y-3">
              <label className="block text-xs">Reviewer emails (record checks and a recommendation)<textarea className="w-full rounded-md border border-[#cfd4dc] px-3 py-2 text-sm" rows={2} value={f.reviewers} onChange={(e) => setF({ ...f, reviewers: e.target.value })} data-testid="terms-settings-reviewers" /></label>
              <label className="block text-xs">Approver emails (second approval; owners and platform admins always count)<textarea className="w-full rounded-md border border-[#cfd4dc] px-3 py-2 text-sm" rows={2} value={f.approvers} onChange={(e) => setF({ ...f, approvers: e.target.value })} data-testid="terms-settings-approvers" /></label>
              <div className="text-xs space-y-0.5" data-testid="terms-settings-reasons">
                <div className="font-medium">Reason codes (fixed)</div>
                {f.reasons.map((r) => <div key={r.code} data-testid={`terms-settings-reason-${r.code}`}><span className="font-mono">{r.code}</span> {r.label} · email {r.email_template}</div>)}
              </div>
              <label className="block text-xs w-40">Cycle window start (MM-DD)<input className="w-full rounded-md border border-[#cfd4dc] px-3 py-2 text-sm" value={f.window} onChange={(e) => setF({ ...f, window: e.target.value })} data-testid="terms-settings-window" /></label>
              <div className="flex items-center gap-3"><button className="px-4 py-1.5 rounded-full bg-[#0072bc] text-white text-sm font-semibold" onClick={save} data-testid="terms-settings-save">Save review settings</button>
                {msg && <span className="text-xs" data-testid="terms-settings-msg">{msg}</span>}</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
